import React, { useState, useEffect } from 'react';
import { chargesApi, api } from '../lib/api';
import { Receipt, Plus, Trash2, Edit2, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const CHARGE_TIMES = [
  { value: 'disbursement', label: 'Disbursement (Initiation Fee)', description: 'Applied once when loan is disbursed' },
  { value: 'account_opening', label: 'Account Opening / Activation', description: 'Applied when a new account is opened (savings, shares, etc.)' },
  { value: 'loan_application', label: 'Loan Application / Processing', description: 'Applied when a loan application is submitted' },
  { value: 'client_membership', label: 'Client Membership / Registration', description: 'Applied when a new client is registered' },
  { value: 'deposit', label: 'Deposit Fee', description: 'Applied per deposit transaction' },
  { value: 'withdrawal', label: 'Withdrawal / Transaction Fee', description: 'Applied per savings withdrawal' },
  { value: 'transfer', label: 'Transfer Fee', description: 'Applied per internal/external transfer' },
  { value: 'share_purchase', label: 'Share Purchase Fee', description: 'Applied when shares are purchased' },
  { value: 'share_redemption', label: 'Share Redemption Fee', description: 'Applied when shares are redeemed/sold back' },
  { value: 'installment_fee', label: 'Installment Fee', description: 'Applied per loan repayment' },
  { value: 'overdue_fee', label: 'Late Payment / Overdue Penalty', description: 'Applied when payment is overdue' },
  { value: 'monthly_fee', label: 'Monthly Service Fee', description: 'Recurring monthly account maintenance' },
  { value: 'annual_fee', label: 'Annual Fee', description: 'Applied once per year' },
  { value: 'closure_fee', label: 'Account Closure Fee', description: 'Applied on account closure' },
];

const APPLIES_TO = [
  { value: 'loan', label: 'Loan Products' },
  { value: 'savings', label: 'Savings / Current Accounts' },
  { value: 'shares', label: 'Share Products' },
  { value: 'lease', label: 'Lease Products' },
  { value: 'all', label: 'All Products' },
];

const ChargesPage = () => {
  const [charges, setCharges] = useState([]);
  const [products, setProducts] = useState({ loans: [], savings: [], leases: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '', charge_type: 'fee', applies_to: 'loan', calculation_type: 'flat',
    amount: '', charge_time: 'disbursement', product_ids: [], is_active: true, nca_capped: true,
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [chRes, lpRes, spRes, leRes] = await Promise.all([
        chargesApi.list(),
        api.get('/loan-products').catch(() => ({ data: [] })),
        api.get('/savings-products').catch(() => ({ data: [] })),
        api.get('/lease-products').catch(() => ({ data: [] })),
      ]);
      setCharges(chRes.data);
      setProducts({ loans: lpRes.data || [], savings: spRes.data || [], leases: leRes.data || [] });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const getRelevantProducts = () => {
    if (form.applies_to === 'loan') return products.loans;
    if (form.applies_to === 'savings') return products.savings;
    if (form.applies_to === 'lease') return products.leases;
    return [...products.loans, ...products.savings, ...products.leases];
  };

  const handleSubmit = async () => {
    if (!form.name || form.amount === '') return toast.error('Name and amount are required');
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editId) {
        await api.put(`/charges/${editId}`, payload);
        toast.success('Charge updated');
      } else {
        await chargesApi.create(payload);
        toast.success('Charge created');
      }
      resetForm();
      load();
    } catch (e) { toast.error('Failed to save charge'); }
  };

  const handleEdit = (ch) => {
    setEditId(ch.id);
    setForm({
      name: ch.name || '', charge_type: ch.charge_type || 'fee', applies_to: ch.applies_to || 'loan',
      calculation_type: ch.calculation_type || 'flat', amount: ch.amount?.toString() || '0',
      charge_time: ch.charge_time || 'disbursement', product_ids: ch.product_ids || [],
      is_active: ch.is_active !== false, nca_capped: ch.nca_capped !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this charge?')) return;
    try { await chargesApi.delete(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditId(null);
    setForm({ name: '', charge_type: 'fee', applies_to: 'loan', calculation_type: 'flat', amount: '', charge_time: 'disbursement', product_ids: [], is_active: true, nca_capped: true });
  };

  const toggleProduct = (pid) => {
    setForm(f => ({
      ...f,
      product_ids: f.product_ids.includes(pid)
        ? f.product_ids.filter(id => id !== pid)
        : [...f.product_ids, pid]
    }));
  };

  const getChargeTimeLabel = (val) => CHARGE_TIMES.find(t => t.value === val)?.label || val;
  const getProductNames = (ch) => {
    if (!ch.product_ids || ch.product_ids.length === 0) return 'All Products';
    const all = [...products.loans, ...products.savings, ...products.leases];
    return ch.product_ids.map(pid => all.find(p => p.id === pid)?.name || pid).join(', ');
  };

  return (
    <div className="space-y-6 fade-in" data-testid="charges-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Charges</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">FEES & PENALTIES</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Charges</h1>
          <p className="text-xs text-gray-500 mt-1">Define fees, penalties, and service charges per product. Set amount to 0 to disable a charge for a product. NCA caps are enforced automatically.</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-charge-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Charge
        </Button>
      </div>

      {/* NCA Info Banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700">
          <span className="font-medium">NCA Compliance:</span> Initiation fees capped at R1,050 + 10% of amount above R10,000. Monthly service fees capped at R69/month. All caps enforced automatically when "NCA Capped" is enabled.
        </div>
      </div>

      {/* Charges Table */}
      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Charge</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Applies To</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Products</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">When</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">NCA</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            charges.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">No charges defined. Click "Add Charge" to create one.</td></tr> :
            charges.map(ch => (
              <tr key={ch.id} className="border-b hover:bg-gray-50" data-testid={`charge-row-${ch.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{ch.name}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 ${ch.is_penalty ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{ch.charge_type === 'penalty' ? 'Penalty' : 'Fee'}</span></td>
                <td className="px-4 py-3 text-xs text-gray-600 capitalize">{ch.applies_to}</td>
                <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate" title={getProductNames(ch)}>{getProductNames(ch)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{getChargeTimeLabel(ch.charge_time)}</td>
                <td className="px-4 py-3 text-sm font-medium text-right">{ch.calculation_type === 'percentage' ? `${ch.amount}%` : `R ${ch.amount?.toFixed(2)}`}</td>
                <td className="px-4 py-3 text-center">{ch.nca_capped !== false ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-xs text-gray-400">No</span>}</td>
                <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 ${ch.is_active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{ch.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(ch)} className="text-gray-500 hover:text-[#C5A059]"><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(ch.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" aria-describedby="charge-form-desc">
          <DialogHeader><DialogTitle>{editId ? 'Edit Charge' : 'Add Charge'}</DialogTitle></DialogHeader>
          <p id="charge-form-desc" className="sr-only">Form to configure a fee or penalty charge</p>
          <div className="space-y-4">
            <div><Label>Charge Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Loan Initiation Fee" data-testid="charge-name" /></div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Charge Type *</Label>
                <Select value={form.charge_type} onValueChange={v => setForm({...form, charge_type: v})}>
                  <SelectTrigger data-testid="charge-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fee">Fee</SelectItem><SelectItem value="penalty">Penalty</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Applies To *</Label>
                <Select value={form.applies_to} onValueChange={v => setForm({...form, applies_to: v, product_ids: []})}>
                  <SelectTrigger data-testid="charge-applies"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPLIES_TO.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Calculation Method *</Label>
                <Select value={form.calculation_type} onValueChange={v => setForm({...form, calculation_type: v})}>
                  <SelectTrigger data-testid="charge-calc"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="flat">Flat Amount (R)</SelectItem><SelectItem value="percentage">Percentage (%)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>{form.calculation_type === 'percentage' ? 'Percentage (%)' : 'Amount (R)'} *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder={form.calculation_type === 'percentage' ? 'e.g., 1.5' : 'e.g., 250'} data-testid="charge-amount" />
                <p className="text-[10px] text-gray-400 mt-1">Set to 0 to disable this charge</p>
              </div>
            </div>

            <div><Label>When to Apply *</Label>
              <Select value={form.charge_time} onValueChange={v => setForm({...form, charge_time: v})}>
                <SelectTrigger data-testid="charge-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHARGE_TIMES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-400 mt-1">{CHARGE_TIMES.find(t => t.value === form.charge_time)?.description}</p>
            </div>

            {/* Product Selection */}
            <div>
              <Label>Link to Products</Label>
              <p className="text-[10px] text-gray-400 mb-2">Select specific products or leave empty to apply to all {form.applies_to} products</p>
              <div className="border border-gray-200 max-h-40 overflow-y-auto p-2 space-y-1">
                {getRelevantProducts().length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-2">No products found</p>
                ) : (
                  getRelevantProducts().map(p => (
                    <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700">{p.name}</span>
                      <span className="text-[10px] text-gray-400 ml-auto">{p.short_name || p.code || ''}</span>
                    </label>
                  ))
                )}
              </div>
              {form.product_ids.length === 0 && <p className="text-[10px] text-amber-600 mt-1">Applies to all {form.applies_to} products</p>}
            </div>

            <div className="flex items-center gap-6 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} data-testid="charge-active-toggle" />
                <Label className="text-sm">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.nca_capped} onCheckedChange={v => setForm({...form, nca_capped: v})} data-testid="charge-nca-toggle" />
                <Label className="text-sm">NCA Capped</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="charge-submit">{editId ? 'Update' : 'Create'} Charge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChargesPage;
